<template>
	<div class="audio-player">
		<!-- 재생/일시정지 버튼 -->
		<button class="play-button" @click="togglePlay">
			<svg v-if="isPlaying" viewBox="0 0 24 24" fill="currentColor">
				<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
			</svg>
			<svg v-else viewBox="0 0 24 24" fill="currentColor">
				<path d="M8 5v14l11-7z" />
			</svg>
		</button>

		<!-- 진행 바 및 시간 표시 영역 -->
		<div class="progress-section">
			<!-- 진행 바 -->
			<div class="progress-bar-container" @click="handleProgressClick">
				<div class="progress-bar-track">
					<div class="progress-bar-fill" :style="{ width: `${progressPercent}%` }">
						<div class="progress-bar-handle" @mousedown="startDragging"></div>
					</div>
				</div>
			</div>

			<!-- 시간 표시 -->
			<div class="time-display">
				<span>{{ formatTime(currentTime) }}</span>
				<span class="time-separator">/</span>
				<span>{{ formatTime(duration) }}</span>
			</div>
		</div>

		<!-- 설정 버튼 및 속도 조절 메뉴 -->
		<div ref="settingsContainer" class="settings-container">
			<button class="settings-button" @click="toggleSpeedMenu">
				<Icon name="setting" :size="20" />
			</button>

			<!-- 속도 조절 메뉴 -->
			<Transition name="fade">
				<div v-if="isSpeedMenuOpen" class="speed-menu">
					<div class="speed-menu-header">
						<button class="speed-menu-back" @click="closeSpeedMenu">
							<svg viewBox="0 0 24 24" fill="currentColor">
								<path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
							</svg>
						</button>
						<span class="speed-menu-title">재생 속도</span>
					</div>

					<div class="speed-control">
						<div class="speed-label">맞춤 설정</div>
						<div class="speed-slider-container">
							<input
								v-model.number="playbackSpeed"
								type="range"
								min="0.5"
								max="2"
								step="0.25"
								class="speed-slider"
								@input="updatePlaybackSpeed"
							/>
							<span class="speed-value">{{ playbackSpeed.toFixed(2) }}x</span>
						</div>
					</div>

					<div class="speed-presets">
						<button
							v-for="preset in speedPresets"
							:key="preset.value"
							class="speed-preset-item"
							:class="{ active: playbackSpeed === preset.value }"
							@click="setPlaybackSpeed(preset.value)"
						>
							<span v-if="playbackSpeed === preset.value" class="check-icon">✓</span>
							<span>{{ preset.label }}</span>
						</button>
					</div>
				</div>
			</Transition>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watchEffect } from 'vue';
import { onClickOutside } from '@vueuse/core';

import { MESSAGE_AUDIO_FILE_NOT_FOUND } from '@/constants/title';
import { usePopupStore } from '@/stores/popup';

import Icon from '../atom/icon/Icon.vue';

interface Props {
	src?: string;
	autoplay?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	src: '',
	autoplay: false,
});

const popupStore = usePopupStore();

const audioRef = ref<HTMLAudioElement | null>(null);
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const progressPercent = ref(0);
const isDragging = ref(false);
const isSpeedMenuOpen = ref(false);
const playbackSpeed = ref(1);
const settingsContainer = ref<HTMLElement | null>(null);

const audioFile = computed(() => {
	return props.src;
});

const speedPresets = [
	{ value: 0.5, label: '0.5' },
	{ value: 0.75, label: '0.75' },
	{ value: 1, label: '보통' },
	{ value: 1.25, label: '1.25' },
	{ value: 1.5, label: '1.5' },
	{ value: 1.75, label: '1.75' },
	{ value: 2, label: '2.0' },
];

// 오디오 ref 초기화 함수
const initializeAudioRef = () => {
	audioRef.value?.pause();
	audioRef.value = null;

	// 관련 상태 초기화
	isPlaying.value = false;
	currentTime.value = 0;
	duration.value = 0;
	progressPercent.value = 0;
	isDragging.value = false;
};

// 오디오 이벤트 리스너 설정
const setupAudioListeners = () => {
	if (!audioRef.value) return;

	audioRef.value.addEventListener('loadedmetadata', () => {
		duration.value = audioRef.value?.duration || 0;
	});

	audioRef.value.addEventListener('timeupdate', () => {
		if (!isDragging.value && audioRef.value) {
			currentTime.value = audioRef.value.currentTime;
			progressPercent.value = audioRef.value.duration
				? (audioRef.value.currentTime / audioRef.value.duration) * 100
				: 0;
		}
	});

	audioRef.value.addEventListener('ended', () => {
		isPlaying.value = false;
		currentTime.value = 0;
		progressPercent.value = 0;
	});
};

// 오디오 초기화 및 src 변경 감지
watchEffect(() => {
	if (audioFile.value) {
		// 기존 오디오가 있으면 src만 변경, 없으면 새로 생성
		if (audioRef.value) {
			audioRef.value.src = audioFile.value;
			audioRef.value.load();
		} else {
			audioRef.value = new Audio(audioFile.value);
		}
		setupAudioListeners();
		if (props.autoplay) {
			audioRef.value.play();
			isPlaying.value = true;
		}
	} else {
		initializeAudioRef();
	}
});

onUnmounted(() => {
	initializeAudioRef();
});

// 재생/일시정지 토글
const togglePlay = async () => {
	if (!audioRef.value) {
		popupStore.openPopup({
			content: MESSAGE_AUDIO_FILE_NOT_FOUND,
		});
		return;
	}

	if (isPlaying.value) {
		audioRef.value.pause();
		isPlaying.value = false;
	} else {
		await audioRef.value.play();
		isPlaying.value = true;
	}
};

// 진행 바 클릭 처리
const handleProgressClick = (event: MouseEvent) => {
	if (!audioRef.value || isDragging.value) return;

	const progressBar = event.currentTarget as HTMLElement;
	const rect = progressBar.getBoundingClientRect();
	const clickX = event.clientX - rect.left;
	const percent = (clickX / rect.width) * 100;
	const newTime = (percent / 100) * duration.value;

	audioRef.value.currentTime = newTime;
	currentTime.value = newTime;
	progressPercent.value = percent;
};

// 드래그 시작
const startDragging = (event: MouseEvent) => {
	event.stopPropagation();
	isDragging.value = true;

	const handleMouseMove = (e: MouseEvent) => {
		if (!audioRef.value || !isDragging.value) return;

		const progressBar = (event.target as HTMLElement).closest('.progress-bar-container') as HTMLElement;
		if (!progressBar) return;

		const rect = progressBar.getBoundingClientRect();
		const clickX = e.clientX - rect.left;
		const percent = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
		const newTime = (percent / 100) * duration.value;

		audioRef.value.currentTime = newTime;
		currentTime.value = newTime;
		progressPercent.value = percent;
	};

	const handleMouseUp = () => {
		isDragging.value = false;
		document.removeEventListener('mousemove', handleMouseMove);
		document.removeEventListener('mouseup', handleMouseUp);
	};

	document.addEventListener('mousemove', handleMouseMove);
	document.addEventListener('mouseup', handleMouseUp);
};

// 시간 포맷팅
const formatTime = (seconds: number): string => {
	if (!isFinite(seconds) || isNaN(seconds)) return '0:00';

	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// 속도 메뉴 토글
const toggleSpeedMenu = () => {
	isSpeedMenuOpen.value = !isSpeedMenuOpen.value;
};

const closeSpeedMenu = () => {
	isSpeedMenuOpen.value = false;
};

// 속도 변경
const setPlaybackSpeed = (speed: number) => {
	playbackSpeed.value = speed;
	updatePlaybackSpeed();
	closeSpeedMenu();
};

const updatePlaybackSpeed = () => {
	if (audioRef.value) {
		audioRef.value.playbackRate = playbackSpeed.value;
	}
};

// 외부 클릭 시 메뉴 닫기
onClickOutside(settingsContainer, () => {
	if (isSpeedMenuOpen.value) {
		closeSpeedMenu();
	}
});
</script>

<style lang="scss" scoped>
.audio-player {
	display: flex;
	align-items: center;
	gap: var(--space-mid);
	padding: var(--space-x-small) var(--space-mid);
	background-color: var(--color-bg-mid);
	border-radius: var(--border-radius-mid);
	position: relative;

	max-width: 28rem;
	width: 100%;
}

.play-button {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 3.2rem;
	height: 3.2rem;
	border-radius: var(--border-radius-full);
	background-color: var(--color-primary);
	color: var(--color-font-white);
	border: none;
	cursor: pointer;
	transition: all var(--transition-default);
	flex-shrink: 0;

	svg {
		width: 1.8rem;
		height: 1.8rem;
	}

	&:hover {
		opacity: 0.9;
		transform: scale(1.05);
	}

	&:active {
		transform: scale(0.95);
	}
}

.progress-section {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: var(--space-x-small);
	min-width: 0;
}

.progress-bar-container {
	width: 100%;
	height: 0.6rem;
	cursor: pointer;
	position: relative;
}

.progress-bar-track {
	width: 100%;
	height: 100%;
	background-color: var(--color-bg-light);
	border-radius: var(--border-radius-full);
	position: relative;
	overflow: hidden;
}

.progress-bar-fill {
	height: 100%;
	background-color: var(--color-primary);
	border-radius: var(--border-radius-full);
	position: relative;
	transition: width 0.1s linear;
}

.progress-bar-handle {
	position: absolute;
	right: -0.8rem;
	top: 50%;
	transform: translateY(-50%);
	width: 1.6rem;
	height: 1.6rem;
	background-color: var(--color-primary);
	border-radius: var(--border-radius-full);
	cursor: grab;
	box-shadow: 0 0.2rem 0.4rem rgba(0, 0, 0, 0.2);

	&:active {
		cursor: grabbing;
	}
}

.time-display {
	display: flex;
	align-items: center;
	gap: 0.4rem;
	font-size: var(--font-s-small);
	color: var(--color-font-white);
	font-weight: var(--font-w-regular);
}

.time-separator {
	color: var(--color-font-mid);
}

.settings-container {
	position: relative;
	flex-shrink: 0;
}

.settings-button {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 3.2rem;
	height: 3.2rem;
	background: transparent;
	border: none;
	cursor: pointer;
	color: var(--color-font-white);
	transition: opacity var(--transition-default);

	&:hover {
		opacity: 0.7;
	}
}

.speed-menu {
	position: absolute;
	top: calc(100% + var(--space-small));
	right: 0;
	width: 28rem;
	background-color: var(--color-bg-light);
	border-radius: var(--border-radius-mid);
	padding: var(--space-mid);
	box-shadow: 0 0.4rem 1.2rem rgba(0, 0, 0, 0.3);
	z-index: 100;
}

.speed-menu-header {
	display: flex;
	align-items: center;
	gap: var(--space-small);
	margin-bottom: var(--space-x-small);
}

.speed-menu-back {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2.4rem;
	height: 2.4rem;
	background: transparent;
	border: none;
	cursor: pointer;
	color: var(--color-font-white);

	svg {
		width: 2rem;
		height: 2rem;
	}

	&:hover {
		opacity: 0.7;
	}
}

.speed-menu-title {
	font-size: var(--font-s-regular);
	font-weight: var(--font-w-medium);
	color: var(--color-font-white);
}

.speed-control {
	margin-bottom: var(--space-mid);
}

.speed-label {
	font-size: var(--font-s-small);
	color: var(--color-font-white);
	margin-bottom: var(--space-x-small);
}

.speed-slider-container {
	display: flex;
	align-items: center;
	gap: var(--space-small);
}

.speed-slider {
	flex: 1;
	height: 0.4rem;
	background: var(--color-bg-mid);
	border-radius: var(--border-radius-full);
	outline: none;
	cursor: pointer;

	&::-webkit-slider-thumb {
		appearance: none;
		width: 1.6rem;
		height: 1.6rem;
		background-color: var(--color-secondary-Blue);
		border-radius: var(--border-radius-full);
		cursor: pointer;
	}

	&::-moz-range-thumb {
		width: 1.6rem;
		height: 1.6rem;
		background-color: var(--color-secondary-Blue);
		border-radius: var(--border-radius-full);
		border: none;
		cursor: pointer;
	}
}

.speed-value {
	font-size: var(--font-s-small);
	color: var(--color-font-white);
	font-weight: var(--font-w-medium);
	min-width: 4rem;
	text-align: right;
}

.speed-presets {
	display: flex;
	flex-direction: column;
	gap: 0.4rem;
}

.speed-preset-item {
	display: flex;
	align-items: center;
	gap: var(--space-small);
	padding: var(--space-x-small) var(--space-small);
	border-radius: var(--border-radius-small);
	background: transparent;
	border: none;
	cursor: pointer;
	color: var(--color-font-white);
	font-size: var(--font-s-small);
	transition: background-color var(--transition-default);
	text-align: left;
	width: 100%;

	&:hover {
		background-color: var(--color-bg-mid);
	}

	&.active {
		background-color: var(--color-bg-mid);
		color: var(--color-primary);
		font-weight: var(--font-w-semi-bold);
	}
}

.check-icon {
	width: 1.6rem;
	height: 1.6rem;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: var(--font-w-bold);
}

.preset-description {
	margin-left: auto;
	font-size: var(--font-s-x-small);
	color: var(--color-font-mid);
}

.fade-enter-active,
.fade-leave-active {
	transition:
		opacity var(--transition-default),
		transform var(--transition-default);
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
	transform: translateY(0.4rem);
}
</style>
